import './BetbookPosts.less';
import React, { useEffect, useLayoutEffect } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';

import { navigate } from '@/routing';
import i18n from 'nike-ui/src/common/i18n';
import { useNikeRouteContext } from 'nike-ui/src/common/routing/NikeRouteContext';
import { ONB_POST_REGISTRATION } from '@/routing/routes';
import BetbookPostItemLoader from '@/betbook/views/BetbookPostItemLoader';
import Button from '@/common/component/Button';
import EmptyState from 'nike-ui/src/components/empty-state/EmptyState';
import InfiniteScroll from '@/responsive/components/InfiniteScroll/InfiniteScroll';
import LoaderMini from '@/ui/views/LoaderMini';
import MultipleComponentsLoader from '@/common/components/loaders/MultipleComponentsLoader';
import { loadBetbookPosts, loadMoreBetbookPosts, setScroll } from '@/betbook/handlers';
import {
    hasNextPage$,
    initialBetbookPostsLoadingDone$,
    loadingBetbookPostsPending$,
    offset$,
    posts$,
    scrollPosition$
} from '@/betbook/subs';
import { POST_ANALYSIS } from 'nike-ui/src/components/betbook/constants';
import { accountVerificationStatus$, loggedIn$ } from '@/account/subs';
import BetbookFilters from '@/betbook/views/BetbookFilters';
import {
    betbookFilterBetCount$,
    betbookFilterExpirationTag$,
    betbookFilterOdds$,
    betbookFilterSportIds$,
    betbookFilterStake$,
    betbookFilterWin$
} from 'nike-ui/src/components/betbook/subs';
import { betbookNewRoute, useBetslipActive } from '@/betbook/utils';
import { BetboookPostsSortTypeOpions } from 'nike-ui/src/components/betbook/constants';
import { useBetbookLastParam } from 'nike-ui/src/components/betbook/utils';
import { BetbookAnalysisItem } from '@/betbook/views/BetbookAnalysisItem';
import FloatingButton from 'nike-ui/src/components/floating-button/FloatingButton';
import clsx from 'clsx';

interface BetbookPostsProps {
    sortType?: BetboookPostsSortTypeOpions;
}
export function BetbookAnalyse({ sortType }: BetbookPostsProps) {
    const { subscribe } = useReframe();
    const betslipActive = useBetslipActive();
    const offset = subscribe(offset$) || 0;
    const route = useNikeRouteContext();
    const [lastParam, setLastParam] = useBetbookLastParam();
    const scrollPosition = subscribe(scrollPosition$) || 0;
    const posts = subscribe(posts$);
    const initialLoadingDone = subscribe(initialBetbookPostsLoadingDone$);
    const loadingPending = subscribe(loadingBetbookPostsPending$);
    const hasNextPage = subscribe(hasNextPage$);
    const loggedIn = subscribe(loggedIn$);
    const verificationStatus = subscribe(accountVerificationStatus$);
    const isVerified = verificationStatus === 'VERIFIED';
    const betbookViewPort = document.querySelector('.layout-main-col');

    const betCount = subscribe(betbookFilterBetCount$);
    const expirationTag = subscribe(betbookFilterExpirationTag$);
    const odds = subscribe(betbookFilterOdds$);
    const sportIds = subscribe(betbookFilterSportIds$);
    const stake = subscribe(betbookFilterStake$);
    const win = subscribe(betbookFilterWin$);
    const postType = POST_ANALYSIS;

    const floatingBtnDisabled = !loggedIn || (loggedIn && !isVerified);

    useLayoutEffect(() => {
        if (betbookViewPort && window.matchMedia('(min-width: 800px)')) {
            betbookViewPort.scrollTo(0, scrollPosition);
        }
    }, [scrollPosition, betbookViewPort]);
    useLayoutEffect(() => {
        if (offset === 0) {
            if (window.matchMedia('(max-width: 799px)').matches) {
                window.onbeforeunload = () => {
                    window.scrollTo(0, 0);
                };
                document.body.className = 'betbook-overflow';
            }
        }
    }, [offset, route]);

    useEffect(() => {
        //how detect other than detail -> list change. for instance: post list -> author's analyses -> detail -> back -> back
        const same = lastParam && lastParam.sortType === sortType && lastParam.postType === postType;
        if (!lastParam || !same) {
            if (lastParam?.postType === postType && lastParam?.sortType !== sortType) {
                dispatch(loadBetbookPosts(postType, sortType, sportIds, odds, stake, win, betCount, expirationTag));
            } else {
                dispatch(loadBetbookPosts(postType, sortType));
            }
            setLastParam({ postType, sortType });
        }
    }, [betCount, expirationTag, lastParam, odds, postType, setLastParam, sortType, sportIds, stake, win]);

    function handlePositionMemoize() {
        betbookViewPort && dispatch(setScroll(betbookViewPort?.scrollTop));
    }
    function handleNewAnalysis() {
        if (loggedIn) {
            navigate(betbookNewRoute(POST_ANALYSIS));
        } else {
            dispatch(['login.openScreen']);
        }
    }
    function handleLoadMore() {
        dispatch(loadMoreBetbookPosts(POST_ANALYSIS));
    }
    return (
        <div onClick={handlePositionMemoize} className={clsx({ 'betslip-visible': betslipActive })}>
            {loggedIn && !isVerified && <NotVerifiedInfo verificationStatus={verificationStatus} />}
            <BetbookFilters postType={POST_ANALYSIS} />
            {initialLoadingDone ? (
                posts && Array.isArray(posts) && posts.length ? (
                    <InfiniteScroll
                        className="betbook-posts"
                        hasMore={hasNextPage && !loadingPending}
                        loadMore={handleLoadMore}
                        loader={<LoaderMini className="infinite-loader betbook-more-posts-loader" key="loader" />}
                        threshold={0}
                        initialLoad={false}
                    >
                        {posts.map(post => (
                            <BetbookAnalysisItem key={post.id} post={post} />
                        ))}
                        <FloatingButton
                            onClick={handleNewAnalysis}
                            dataAtid="betbook-new-post"
                            disabled={floatingBtnDisabled}
                        />
                    </InfiniteScroll>
                ) : (
                    <div className="betbook-posts">
                        <EmptyState text={i18n('betbook.posts.empty')} />
                        <FloatingButton
                            onClick={handleNewAnalysis}
                            dataAtid="betbook-new-post"
                            disabled={floatingBtnDisabled}
                        />
                    </div>
                )
            ) : (
                <div className="betbook-posts">
                    <MultipleComponentsLoader itemsCount={2} componentLoader={BetbookPostItemLoader} />
                </div>
            )}
        </div>
    );
}

function NotVerifiedInfo({ verificationStatus }) {
    function handleCompleteRegistration(e) {
        e.stopPropagation();
        navigate(ONB_POST_REGISTRATION);
    }

    return (
        <div className="betbook-verification">
            {verificationStatus === 'UNVERIFIED' ? i18n('betbook.notVerified.text') : i18n('betbook.verifying.text')}
            {verificationStatus === 'UNVERIFIED' && (
                <Button className="finish-registration-link" onClick={handleCompleteRegistration}>
                    {i18n('registration.leave.action.stay')}
                </Button>
            )}
        </div>
    );
}
